import { useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get, trim, some } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiDelete from 'lib/network/apiDelete'
import { useHistory, useLocation } from 'react-router-dom'
import Switch from 'components/atoms/Switch'
import InfiniteScroll from 'react-infinite-scroll-component'

import Blockies from 'react-blockies'

import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import { startEndTruncate } from 'utils'

import notify from 'components/atoms/Notify'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

import { useSelector } from 'react-redux'
import apiPut from 'lib/network/apiPut'
import { getBlogStatus, getScheduleDate } from 'utils/blogPublish'

const getStatusBadgeClass = status => {
  if (status === 'published') return 'bg-green-100 text-green-800 border border-green-200'
  if (status === 'scheduled') return 'bg-blue-100 text-blue-800 border border-blue-200'
  return 'bg-gray-100 text-gray-800 border border-gray-200'
}

const getStatusLabel = status => {
  if (status === 'published') return 'Published'
  if (status === 'scheduled') return 'Scheduled'
  return 'Draft'
}

const BlogListing = () => {
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pagination, setPagination] = useState({})
  const [blogs, setBlogs] = useState([])
  const [search, setSearch] = useState('')

  const userFromStore = useSelector(state => get(state, 'user.user'))
  const hasAdminAccess = userFromStore?.roles && some(userFromStore.roles, { type: 'admin' })

  useEffect(() => {
    setBlogs([]) // Reset blogs when search changes to prevent duplicates
    fetchBlogs(1)
  }, [search])

  const fetchBlogs = async (page = 1) => {
    // Ensure page is always a valid number
    const pageNumber = Number.isInteger(page) && page > 0 ? page : 1

    if (pageNumber === 1) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    const params = {}
    params.pageNumber = pageNumber
    params.pageSize = 20
    params.query = search || undefined

    try {
      const response = await apiGet('admin-blogs', params)

      setBlogs(pageNumber === 1 ? response.data.blogs : [...blogs, ...response.data.blogs])
      setPagination(response?.data?.pagination || {})
    } catch (error) {
      notify.error('Error loading blogs')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleDelete = blogId => {
    notify.deleteConfirm({
      onConfirm: async () => {
        try {
          const response = await apiDelete(`blog/${blogId}`)
          if (response?.success) {
            notify.success('Blog deleted successfully')
            fetchBlogs(1)
          } else {
            notify.error(response?.message || 'Failed to delete blog')
          }
        } catch (error) {
          notify.error('Error deleting blog')
        }
      },
      dangerText: 'Delete',
      title: 'Delete Blog?',
      message: 'Are you sure you want to delete this blog? This action cannot be undone.',
    })
  }

  const handlePublishedToggle = async (blogId, published) => {
    try {
      const payload = { published }
      const response = await apiPut(`blog/${blogId}`, payload)
      if (response?.success) {
        const updatedBlog = response?.data || {}
        setBlogs(prevBlogs =>
          prevBlogs.map(blog =>
            blog._id === blogId
              ? {
                  ...blog,
                  ...updatedBlog,
                  published: updatedBlog.published ?? published,
                  effectivePublishDate: updatedBlog.effectivePublishDate ?? null,
                }
              : blog
          )
        )
        notify.success(published ? 'Blog published successfully' : 'Blog unpublished successfully')
      } else {
        notify.error(response?.message || 'Failed to update blog status')
      }
    } catch (error) {
      notify.error('Error updating blog status')
    }
  }

  return (
    <Layout
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <Button onClick={() => history.push('/add/blogs')} className="w-full sm:w-auto">
            Create New Blog
          </Button>
        </div>
      }
      title="Blogs"
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              {/* Search */}

              <div className="min-w-full">
                {!loading ? (
                  <>
                    {blogs?.length === 0 ? (
                      <EmptyState v2 description="No blogs found. Create your first blog!" />
                    ) : (
                      <InfiniteScroll
                        dataLength={blogs.length}
                        next={() => {
                          if (!loadingMore && pagination?.currentPage !== pagination?.totalPages) {
                            const nextPage = pagination?.currentPage ? Number(pagination.currentPage) + 1 : 2
                            fetchBlogs(nextPage)
                          }
                        }}
                        hasMore={pagination?.currentPage !== pagination?.totalPages && !loadingMore}
                        loader={
                          loadingMore ? (
                            <div className="w-full py-8 flex justify-center items-center">
                              <LoadingIcon />
                            </div>
                          ) : null
                        }
                        scrollThreshold={0.8}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 pb-8">
                          {blogs.map(blog => {
                            const status = getBlogStatus(blog)
                            return (
                              <div
                                key={blog._id}
                                className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col"
                              >
                                {/* Featured Image */}
                                <div className="relative h-48 w-full bg-gray-100">
                                  {blog.featuredImage ? (
                                    <img src={blog.featuredImage} alt={blog.title || 'Blog'} className="w-full h-full object-cover" />
                                  ) : blog.images?.[0] ? (
                                    <img src={blog.images[0]} alt={blog.title || 'Blog'} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Blockies seed={blog._id || ''} size={8} scale={8} className="rounded" />
                                    </div>
                                  )}
                                  {/* Status Badge */}
                                  <div className="absolute top-3 right-3">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>{getStatusLabel(status)}</span>
                                  </div>
                                  {/* Category Badge */}
                                  {blog.category && (
                                    <div className="absolute top-3 left-3">
                                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">{blog.category}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Content */}
                                <div className="p-5 flex-1 flex flex-col">
                                  {/* Title */}
                                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]">{trim(blog.title) || 'Untitled'}</h3>

                                  {/* Description */}
                                  <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-1">
                                    {blog.description ? startEndTruncate(blog.description, 120) : 'No description available'}
                                  </p>

                                  {/* Meta Information */}
                                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-center space-x-2">
                                      <span>Slug:</span>
                                      <span className="font-mono text-gray-400">{blog.slug || '-'}</span>
                                    </div>
                                    <div className="flex flex-col items-end space-y-1">
                                      {status === 'scheduled' && (
                                        <span className="text-blue-600 font-medium">Publishes {moment(getScheduleDate(blog)).format('MMM D, YYYY [at] h:mm A')}</span>
                                      )}
                                      <span>{moment(blog.createdAt).fromNow()}</span>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-xs text-gray-600">Published:</span>
                                      <Switch value={status === 'published'} onChange={published => handlePublishedToggle(blog._id, published)} />
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          history.push(`/blogs/edit/${blog._id}/blog-profile${location.search}`)
                                        }}
                                      >
                                        Edit blogs
                                      </Button>
                                      {hasAdminAccess && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleDelete(blog._id)}
                                          className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                                        >
                                          Delete
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </InfiniteScroll>
                    )}
                  </>
                ) : (
                  <div className="w-full min-h-[60vh] flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default BlogListing
